import update from 'immutability-helper';

export interface IWizardStep {
    id: string;
    visited?: boolean;
    params?: any;
}

export type WizardNavigationStatus = 'idle' | 'navigating';

export interface IWizardState {
    navigation: {
        status: WizardNavigationStatus;
        args?: { to: string; from: string; forward?: boolean; params?: any; }
    };
    currentStepId: string;
    stepIds: string[];
    steps: { [key: string]: IWizardStep; };
    stepsData: { [key: string]: any; };
}

type WizardAction = { type: 'start-navigate'; payload: { to: string; params?: any; }; } |
{ type: 'cancel-navigate'; payload: { to: string; }; } |
{ type: 'end-navigate'; payload: { to: string; }; } |
{ type: 'save-step-data'; payload: { stepId: string; data: any; }; } |
{ type: 'add'; payload: IWizardStep; } |
{ type: 'remove'; payload: string; } | { type: 'clear-all-steps-data' };

const selectStepIndex = (state: IWizardState, stepId: string) => state.stepIds.findIndex(i => i === stepId);
const isStepInRange = (state: IWizardState, stepIndex: number) => stepIndex >= 0 && stepIndex < state.stepIds.length;
const isForwardNavigation = (state: IWizardState, to: string) => selectStepIndex(state, to) < selectStepIndex(state, state.currentStepId);

const setNavigationStatusToIdle = (state: IWizardState) => update(state, {
    navigation: {
        status: { $set: 'idle' },
        $unset: ['args']
    }
});

const setCurrentStepAsVisited = (state: IWizardState) => update(state, {
    steps: {
        [state.currentStepId]: { visited: { $set: true } }
    }
});


const setCurrentStepParams = (state: IWizardState, params?: any) => update(state, {
    steps: {
        [state.currentStepId]: {
            visited: { $set: true },
            params: { $set: params }
        }
    }
});


const navigateToStep = (state: IWizardState, nextStepIndex: number) => setCurrentStepAsVisited(
    setCurrentStepParams(
        setNavigationStatusToIdle(
            update(state, {
                currentStepId: {
                    $apply: (oldValue) => {
                        const currentStepIndex = selectStepIndex(state, oldValue);
                        const isNextStepInRange = isStepInRange(state, nextStepIndex);
                        return state.stepIds[isNextStepInRange ? nextStepIndex : currentStepIndex];
                    }
                },
                navigation: { status: { $set: 'idle' } }
            })
        ), state.navigation.args?.params)
);

export const wizardReducer = (state: IWizardState, action: WizardAction) => {
    switch (action.type) {
        case 'start-navigate':
            return update(state, {
                navigation: {
                    status: { $set: 'navigating' },
                    args: {
                        $set: {
                            from: state.currentStepId,
                            to: action.payload.to,
                            forward: isForwardNavigation(state, action.payload.to),
                            params: action.payload.params
                        }
                    }
                }
            });

        case 'cancel-navigate':
            return setNavigationStatusToIdle(state);

        case 'end-navigate':
            return navigateToStep(state, selectStepIndex(state, action.payload.to));

        case 'save-step-data':
            return update(state, {
                stepsData: {
                    [action.payload.stepId]: { $set: action.payload.data }
                }
            });

        case 'clear-all-steps-data':
            return update(state, {
                stepsData: { $set: {} }
            });

        case 'add':
            state = update(state, {
                currentStepId: { $apply: (oldValue) => ((state.stepIds.length === 0 && state.currentStepId.length === 0) ? action.payload.id : oldValue) },
                steps: { [action.payload.id]: { $set: action.payload } }
            });
            return update(state, { stepIds: { $push: [action.payload.id] } });
        case 'remove': {
            state = update(state, { steps: { $unset: [action.payload] } });
            const stepIndex = state.stepIds.findIndex(i => i !== action.payload);
            state = update(state, { stepIds: { $splice: [[stepIndex, 1]] } });
            return update(state, { currentStepId: { $apply: (oldValue) => (state.stepIds.length === 0 ? '' : oldValue) } });
        }
        default:
            return state;
    }
};
